import React, { useEffect, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import Loader from '../Loader/Loader';

import { getQrCode } from './lib/generateQrCode';

/**
 * @param {import('./lib/propTypes').QrCodeProps} props
 */
const QrCode = ({
  value,
  size = 256,
  bgColor = '#FFFFFF',
  fgColor = '#000000',
  hoverColor,
  clickColor,
  margin = 0,
  className,
  ...rest
}) => {
  const [qrUrl, setQrUrl] = useState(null);
  const [hoverQrUrl, setHoverQrUrl] = useState(null);
  const [clickQrUrl, setClickQrUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  useEffect(() => {
    setQrUrl(null);
    setHoverQrUrl(null);
    setClickQrUrl(null);
    setError(null);

    const generateQrCodes = async () => {
      try {
        const normalQr = await getQrCode({ url: value, size, bgColor, fgColor, margin, ...rest });
        setQrUrl(normalQr);

        if (hoverColor) {
          const hoverQr = await getQrCode({
            url: value,
            size,
            bgColor,
            fgColor: hoverColor,
            margin,
            ...rest
          });
          setHoverQrUrl(hoverQr);
        }

        if (clickColor) {
          const clickQr = await getQrCode({
            url: value,
            size,
            bgColor,
            fgColor: clickColor,
            margin,
            ...rest
          });
          setClickQrUrl(clickQr);
        }
      } catch (err) {
        setError(err);
      }
    };

    generateQrCodes();
  }, [value, size, bgColor, fgColor, hoverColor, clickColor, margin, JSON.stringify(rest)]);

  const containerStyle = {
    width: size,
    height: size,
    background: bgColor,
    color: fgColor
  };

  if (error) {
    return (
      <div className="flex items-center justify-center" style={containerStyle}>
        <div className="text-red-500 text-center text-xs">{error.message}</div>
      </div>
    );
  }

  if (!qrUrl) {
    return (
      <div className="flex items-center justify-center" style={containerStyle}>
        <Loader />
      </div>
    );
  }

  const getCurrentQrUrl = () => {
    if (isClicked && clickQrUrl) return clickQrUrl;
    if (isHovered && hoverQrUrl) return hoverQrUrl;
    return qrUrl;
  };

  const handleMouseEnter = () => {
    if (hoverColor) setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsClicked(false);
  };

  const handleMouseDown = () => {
    if (clickColor) setIsClicked(true);
  };

  const handleMouseUp = () => {
    setIsClicked(false);
  };

  const isInteractive = hoverColor || clickColor;

  return (
    <div
      className={cn('relative', isInteractive && 'cursor-pointer', className)}
      style={containerStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}>
      <img src={getCurrentQrUrl()} alt="QR Code" className="h-full w-full object-contain" />
    </div>
  );
};

export default QrCode;
